import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AlertButtonType, buttonColorType } from '../../../interfaces/Element.interfaces';
import { DimmedBackground } from '../../common/dimmed/DimmedBackground';

export type AlertItemType = {
  id: string;
  title?: string;
  content?: string;
  buttonArray: AlertButtonType[];
  close: () => void;
};

export const Alert = ({ id, title, content, buttonArray, close }: AlertItemType) => {
  const [visible, setVisible] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout>(setTimeout(() => {}));

  const location = useLocation();
  const [modalOpenedUrl, setModalOpenedUrl] = useState<string>(location.pathname);

  useEffect(() => {
    disableScroll();
    clearTimeout(timeoutRef.current);
    setVisible(true);
    return () => {
      enableScroll();
    };
  }, []);

  function enableScroll() {
    document.body.style.cssText = '';
    document.body.style.overflow = 'unset';
  }
  function disableScroll() {
    document.body.style.overflow = 'hidden';
  }

  useEffect(() => {
    if (modalOpenedUrl !== location.pathname) {
      close();
    }
  }, [modalOpenedUrl, location.pathname, close]);

  return (
    <DimmedBackground isShow onClick={() => {}}>
      <AlertWrapper
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {title && <Title>{title}</Title>}
        {content && <Content>{content}</Content>}

        <ButtonContainer>
          {buttonArray?.map((button) => {
            return (
              <Button
                key={button.text}
                onClick={() => {
                  button.onClick();
                  close();
                }}
                color={button.color}
              >
                <ButtonText color={button.color}>{button.text}</ButtonText>
              </Button>
            );
          })}
        </ButtonContainer>
      </AlertWrapper>
    </DimmedBackground>
  );
};

const AlertWrapper = styled.div`
  width: 300px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 24px;

  gap: 24px;

  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const Title = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */

  margin-top: 16px;
`;

const Content = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const ButtonContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 8px;
`;

const Button = styled.button<{ color: buttonColorType }>`
  width: 100%;
  height: 40px;
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  padding: 16px;

  gap: 4px;

  border-radius: 8px;

  background-color: ${(props) => (props.color === 'blue' ? '#445EFF' : props.color === 'red' ? '#FF3049' : '#F2F2F7')};
`;

const ButtonText = styled.span<{ color: buttonColorType }>`
  color: ${(props) => (props.color === 'gray' ? '#858894' : '#FFFFFF')};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
